import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertTriangle } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { useTranslation } from 'react-i18next'
import { type Section } from '@/types/sections'

interface DeleteSectionDialogProps {
  section: Section
  open: boolean
  onOpenChange: (open: boolean) => void
  onSectionDeleted?: () => void
}

export const DeleteSectionDialog = ({
  section,
  open,
  onOpenChange,
  onSectionDeleted
}: DeleteSectionDialogProps) => {
  const { t } = useTranslation(['common', 'navigation'])
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setError('')
      setIsLoading(false)
    }
  }, [open])

  const handleDelete = async () => {
    setIsLoading(true)
    setError('')

    try {
      await sdk.sections.delete(section.id)
      onOpenChange(false)
      onSectionDeleted?.()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('common:errors.deleteFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <DialogTitle>{t('navigation:sidebar.deleteSection')}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="pt-2">
            {t('common:sections.deleteConfirmation', { title: section.title })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? t('common:actions.deleting') : t('common:actions.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
